<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";

interface Props {
  modelValue?: string | null;
  /** Always visible input (no expand/collapse) */
  fixed?: boolean;
  /** Mobile variant: visible on small screens, hidden on desktop */
  mobile?: boolean;
  placeholder?: string;
  /** Characters needed before the value is emitted, shorter input emits "" (0 emits every keystroke) */
  minLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  fixed: false,
  mobile: false,
  placeholder: undefined,
  minLength: 3,
});

const emit = defineEmits(["update:modelValue"]);

const isToggled = ref(false);
const searchBar = ref<HTMLInputElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
// Kept apart from modelValue, which stays "" below minLength: a re-render would wipe the draft
const inputValue = ref(props.modelValue ?? "");
// Last value exchanged with the parent, to tell our own echo from an external update
let syncedValue = inputValue.value;

const isAlwaysOpen = computed(() => props.fixed || props.mobile);

const resolvedPlaceholder = computed(() => {
  if (props.placeholder !== undefined) return props.placeholder;
  // Only fixed desktop variants had a default placeholder in the originals
  return props.fixed && !props.mobile ? "Rechercher" : undefined;
});

async function triggerSearchBar() {
  if (isAlwaysOpen.value) return;
  isToggled.value = true;
  if (searchInput.value) {
    await nextTick();
    searchInput.value.focus();
  }
}

function unTriggerSearchbar(event: any) {
  if (isAlwaysOpen.value) return;
  if (searchBar.value && !searchBar.value.contains(event.target) && !searchInput.value?.value.length) {
    isToggled.value = false;
  }
}

// A term is re-emitted even when unchanged (retyping it refetches), the "" reset only once
function emitModelValue(value: string) {
  if (!value && !syncedValue) return;
  syncedValue = value;
  emit("update:modelValue", value);
}

function onInput(event: Event) {
  inputValue.value = (event.target as HTMLInputElement).value;
  emitModelValue(inputValue.value.trim().length >= props.minLength ? inputValue.value : "");
}

const clearInput = () => {
  inputValue.value = "";
  emitModelValue("");
};

onMounted(async () => {
  if (!isAlwaysOpen.value) {
    document.addEventListener("click", unTriggerSearchbar);
    if (props.modelValue?.length) {
      await triggerSearchBar();
    }
  }
});

onUnmounted(() => {
  if (!isAlwaysOpen.value) {
    document.removeEventListener("click", unTriggerSearchbar);
  }
});

watch(
  () => props.modelValue,
  (value) => {
    if ((value ?? "") !== syncedValue) {
      syncedValue = value ?? "";
      inputValue.value = syncedValue;
    }
    if (!isAlwaysOpen.value && value?.length) {
      triggerSearchBar();
    }
  },
);
</script>

<template>
  <div
    id="search_bar"
    ref="searchBar"
    class="search_container body_regular relative"
    :class="{ mobile: mobile, expandable: !isAlwaysOpen }"
    :data-toggled="isAlwaysOpen || isToggled"
    @click="triggerSearchBar"
  >
    <SvgIcon class="search_img" name="search" />
    <input
      ref="searchInput"
      :value="inputValue"
      :data-toggled="isAlwaysOpen || isToggled"
      :placeholder="resolvedPlaceholder"
      class="search_input"
      type="text"
      @input="onInput"
    />
    <SvgIcon
      v-if="inputValue && (isAlwaysOpen || isToggled)"
      name="close"
      class="search_clear"
      @click.stop="clearInput"
    />
  </div>
</template>

<style scoped lang="scss">
.search_container {
  @apply flex-row justify-center items-center rounded-[8px];
  color: var(--text-main-color);
  border: 1px solid var(--primary-blue-color);
  background-color: var(--white-main);

  // Desktop by default, Mobile variant inverts
  &:not(.mobile) {
    @apply hidden sm:flex;
  }
  &.mobile {
    @apply flex sm:hidden w-full;
    height: 40px;
    border-color: var(--grey-lightest);

    .search_img {
      @apply mx-[8px];
      width: 16px;
      height: 16px;
      min-width: 16px;
    }

    input {
      margin: 0 4px;
      line-height: 20px;
      height: 100%;
    }

    &:focus,
    &:active,
    &:focus-within {
      border: 1px solid var(--primary-blue-color);
    }
  }

  // Expandable (default desktop) — collapses when not toggled
  &.expandable {
    @apply h-[32px] min-w-[32px];
    cursor: pointer;
    transition: width 0.4s;

    &[data-toggled="false"] {
      &:hover {
        background-color: var(--grey-light);
        border-color: var(--blue-dark);
        color: var(--blue-dark);

        & > * {
          color: var(--blue-dark);
        }
      }
      &:focus,
      &:active {
        background-color: var(--grey-lightest);
      }
    }

    &[data-toggled="true"] {
      cursor: text;
    }

    .search_input {
      visibility: hidden;
      width: 0;
      transition: width 0.4s;

      &[data-toggled="true"] {
        width: 197px;
        padding-right: 28px;
        cursor: text;
        visibility: visible;
      }
    }
  }

  // Fixed desktop — always open (height overridable via --search-bar-height)
  &:not(.expandable):not(.mobile) {
    height: var(--search-bar-height, 32px);
    min-width: 40px;
    cursor: text;

    .search_input {
      width: 100%;
      height: 100%;
      padding-right: 28px;
    }

    .search_img {
      width: 24px;
      height: 24px;
    }
  }

  // Common hover/focus
  &:hover {
    background-color: var(--grey-light);
  }

  &:has(.search_input:focus) {
    border: 1px solid var(--blue-dark);
    background-color: var(--white-main);

    .search_img {
      color: var(--blue-dark);
    }
  }

  &:has(.search_input:active) {
    background-color: var(--grey-light);
  }

  .search_input {
    @apply flex items-center w-full;
    height: 100%;
    background: none;
  }

  .search_img {
    margin-right: 7px;
    margin-left: 7px;
    width: 16px;
    height: 16px;
    color: var(--primary-blue-color);
  }

  .search_clear {
    @apply absolute w-[16px] h-[16px] cursor-pointer right-[7px];
    color: var(--primary-blue-color);
  }
}
</style>
